// Types for filter state management

export enum ReportPeriod {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  ANNUAL = 'annual',
  CUSTOM = 'custom'
}

export enum ComparisonType {
  NONE = 'none',
  MOM = 'mom', // Month over Month
  YOY = 'yoy', // Year over Year
  QOQ = 'qoq', // Quarter over Quarter
  CUSTOM = 'custom' // Custom period comparison
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export enum FilterLogic {
  AND = 'and',
  OR = 'or'
}

export interface MetricThreshold {
  metric: string;
  operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte'; // greater than, less than, equal, etc.
  value: number;
}

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface GroupConfig {
  field: string;
  subGroupBy?: string; // For nested grouping
}

export interface DateRange {
  start: string | null; // ISO format date string
  end: string | null; // ISO format date string
}

export interface QuarterPeriod {
  quarter: 1 | 2 | 3 | 4;
  year: number;
}

export interface YearPeriod {
  year: number;
  fiscal: boolean; // Whether to use fiscal year instead of calendar year
}

export interface MonthPeriod {
  month: number; // 1-12
  year: number;
}

export interface FilterState {
  reportPeriod: ReportPeriod;
  
  // Period-specific configurations
  monthPeriod?: MonthPeriod;
  quarterPeriod?: QuarterPeriod;
  yearPeriod?: YearPeriod;
  customDateRange?: DateRange;
  
  // Comparison configuration
  comparisonType: ComparisonType;
  comparisonPeriod?: {
    monthPeriod?: MonthPeriod;
    quarterPeriod?: QuarterPeriod;
    yearPeriod?: YearPeriod;
    customDateRange?: DateRange;
  };
  
  // Filtering
  sources: string[];
  campaigns: string[];
  trades: string[];
  metricThresholds: MetricThreshold[];
  filterLogic: FilterLogic;
  
  // Sorting & Grouping
  sortConfig: SortConfig[];
  groupConfig?: GroupConfig;
  
  // UI State
  showZeroValues: boolean;
  expandedGroups: string[]; // IDs of expanded groups
}

// Actions for reducer pattern
export enum FilterActionType {
  SET_REPORT_PERIOD = 'SET_REPORT_PERIOD',
  SET_MONTH_PERIOD = 'SET_MONTH_PERIOD',
  SET_QUARTER_PERIOD = 'SET_QUARTER_PERIOD',
  SET_YEAR_PERIOD = 'SET_YEAR_PERIOD',
  SET_CUSTOM_DATE_RANGE = 'SET_CUSTOM_DATE_RANGE',
  SET_COMPARISON_TYPE = 'SET_COMPARISON_TYPE',
  SET_COMPARISON_PERIOD = 'SET_COMPARISON_PERIOD',
  ADD_SOURCE_FILTER = 'ADD_SOURCE_FILTER',
  REMOVE_SOURCE_FILTER = 'REMOVE_SOURCE_FILTER',
  SET_SOURCE_FILTERS = 'SET_SOURCE_FILTERS',
  ADD_CAMPAIGN_FILTER = 'ADD_CAMPAIGN_FILTER',
  REMOVE_CAMPAIGN_FILTER = 'REMOVE_CAMPAIGN_FILTER',
  SET_CAMPAIGN_FILTERS = 'SET_CAMPAIGN_FILTERS',
  ADD_TRADE_FILTER = 'ADD_TRADE_FILTER',
  REMOVE_TRADE_FILTER = 'REMOVE_TRADE_FILTER',
  SET_TRADE_FILTERS = 'SET_TRADE_FILTERS',
  ADD_METRIC_THRESHOLD = 'ADD_METRIC_THRESHOLD',
  REMOVE_METRIC_THRESHOLD = 'REMOVE_METRIC_THRESHOLD',
  UPDATE_METRIC_THRESHOLD = 'UPDATE_METRIC_THRESHOLD',
  SET_FILTER_LOGIC = 'SET_FILTER_LOGIC',
  ADD_SORT_CONFIG = 'ADD_SORT_CONFIG',
  REMOVE_SORT_CONFIG = 'REMOVE_SORT_CONFIG',
  UPDATE_SORT_CONFIG = 'UPDATE_SORT_CONFIG',
  SET_GROUP_CONFIG = 'SET_GROUP_CONFIG',
  TOGGLE_SHOW_ZERO_VALUES = 'TOGGLE_SHOW_ZERO_VALUES',
  TOGGLE_GROUP_EXPANDED = 'TOGGLE_GROUP_EXPANDED',
  RESET_FILTERS = 'RESET_FILTERS',
  LOAD_SAVED_VIEW = 'LOAD_SAVED_VIEW',
  IMPORT_FROM_URL = 'IMPORT_FROM_URL'
}

export interface FilterAction {
  type: FilterActionType;
  payload?: any;
}

// Saved view interface
export interface SavedView {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  isShared?: boolean;
  category?: string;
  filterState: FilterState;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}